import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1SerievideoSeriesSerieidQueryOptions,
  usePostApiV1SerievideoSeriesSerieid,
  usePutApiV1SerievideoId,
} from '@/shared/api/generated';
import type { SerieVideoFormValues } from '../types/serieVideoForm';

/** Callbacks do salvamento de Vídeo. */
export interface SaveSerieVideoCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um Vídeo de uma Série.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/serievideo/series/{serieId}`
 * e `PUT /api/v1/serievideo/{id}`, ambos multipart/form-data), escolhendo a
 * operação pela presença de `id` e invalidando o cache da listagem no sucesso.
 * Mapeia `SerieVideoFormValues` (camelCase, estado do formulário) para o
 * payload PascalCase esperado pelo contrato gerado.
 *
 * @param serieId - ID da série a que o vídeo pertence
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSaveSerieVideo(serieId);
 * save(values, editingVideoId, { onSuccess, onError });
 * ```
 */
export function useSaveSerieVideo(serieId: number) {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1SerievideoSeriesSerieid();
  const updateMutation = usePutApiV1SerievideoId();

  const invalidate = () =>
    queryClient.invalidateQueries(getApiV1SerievideoSeriesSerieidQueryOptions(serieId));

  const save = (
    values: SerieVideoFormValues,
    id: number | undefined,
    callbacks?: SaveSerieVideoCallbacks,
  ) => {
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };
    const onError = () => callbacks?.onError?.();

    if (id === undefined) {
      createMutation.mutate(
        {
          serieId,
          data: {
            Date: values.date,
            Title: values.title,
            Resumee: values.description,
            Embed: values.channel,
            Active: values.active,
            ...(values.thumbnailFile ? { Thumbnail: values.thumbnailFile } : {}),
          },
        },
        { onSuccess, onError },
      );
    } else {
      updateMutation.mutate(
        {
          id,
          params: { serieId },
          data: {
            Date: values.date,
            Title: values.title,
            Resumee: values.description,
            Embed: values.channel,
            Active: values.active,
            ...(values.thumbnailFile ? { Thumbnail: values.thumbnailFile } : {}),
            ...(values.removeThumbnail ? { RemoveThumbnail: true } : {}),
          },
        },
        { onSuccess, onError },
      );
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
